import React, { useState } from "react";
import { toast, Toaster } from "sonner";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import UserPool from "../../Userpool";

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Password visibility states
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("All fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("New password and confirm password do not match");
      return;
    }

    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])[A-Za-z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]{8,}$/;

    if (!passwordRegex.test(newPassword)) {
      toast.error(
        "Password must contain at least 8 characters, including uppercase, lowercase, numbers, and special characters."
      );
      return;
    }

    setIsLoading(true);

    try {
      console.log(
        "ChangePassword: Attempting to get current user from UserPool"
      );
      const user = UserPool.getCurrentUser();

      if (!user) {
        toast.error("User session not found. Please log in again.");
        setIsLoading(false);
        return;
      }

      user.getSession((err, session) => {
        if (err || !session || !session.isValid()) {
          toast.error("Your session has expired. Please log in again.");
          setIsLoading(false);
          return;
        }

        user.changePassword(currentPassword, newPassword, (err, result) => {
          setIsLoading(false);

          if (err) {
            console.error("Change password error:", err);
            if (err.code === "InvalidPasswordException") {
              toast.error(
                "Password does not meet AWS Cognito requirements. Please ensure it meets all the criteria shown below."
              );
            } else if (err.code === "NotAuthorizedException") {
              toast.error("Current password is incorrect.");
            } else if (err.code === "InvalidParameterException") {
              toast.error("Invalid password format.");
            } else if (err.code === "LimitExceededException") {
              toast.error(
                "Too many password change attempts. Please try again later."
              );
            } else {
              toast.error(
                err.message || "Failed to change password. Please try again."
              );
            }
            return;
          }

          if (result === "SUCCESS") {
            toast.success("Password changed successfully!");
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
          }
        });
      });
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error("An unexpected error occurred. Please try again.");
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  return (
    <>
      <div className="tw-flex   tw-items-start tw-w-full tw-justify-center tw-h-screen tw-bg-[#f3f3f3f0]">
        <div className="tw-w-full tw-max-w-5xl tw-px-4 tw-mt-20">
          <div className="tw-rounded-xl tw-shadow-lg tw-w-full tw-bg-white tw-p-4">
            <div className="tw-px-8 tw-py-8 tw-flex tw-items-center tw-border-b tw-border-gray-200 tw-border-l-4 tw-ml-[-18px] tw-border-l-[#0052CC] tw-relative">
              <div className="absolute left-0 top-0 bottom-0 bg-ln-blue w-2 h-20"></div>
              <div className="tw-pl-2">
                <p className="tw-text-2xl tw-font-semibold tw-text-black">
                  Change Password
                </p>
              </div>
            </div>

            <form onSubmit={handleChangePassword}>
              <div className="tw-px-8 tw-py-4">
                <p className="tw-text-gray-600 tw-text-sm tw-mb-4">
                  Enter your current password and choose a new secure password
                  that meets all requirements.
                </p>
              </div>
              <div className="tw-px-8 tw-py-4 tw-flex">
                <div className="tw-flex tw-justify-start tw-w-full">
                  <div className="tw-w-1/2 tw-mr-20 tw-flex tw-flex-col tw-gap-6">
                    <div>
                      <label className="tw-block tw-text-lg tw-font-medium tw-text-black">
                        Current Password{" "}
                        <span className="tw-text-red-500">*</span>
                      </label>
                      <div className="tw-relative">
                        <input
                          type={showCurrentPassword ? "text" : "password"}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="tw-mt-1 tw-px-2 tw-pr-10 input-outline tw-bg-white tw-border-[#CED4DA] tw-focus:ring-ln-blue tw-focus:border-ln-blue tw-block tw-w-full tw-shadow-sm tw-sm:text-sm tw-h-11 tw-rounded-md"
                          placeholder="Enter your current password"
                        />
                        <button
                          type="button"
                          className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-px-2 tw-text-gray-500 hover:tw-text-gray-700 tw-mt-1"
                          onClick={() =>
                            setShowCurrentPassword(!showCurrentPassword)
                          }
                        >
                          {showCurrentPassword ? (
                            <MdVisibilityOff className="tw-h-5 tw-w-5" />
                          ) : (
                            <MdVisibility className="tw-h-5 tw-w-5" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="tw-block tw-text-lg tw-font-medium tw-text-black">
                        Confirm New Password{" "}
                        <span className="tw-text-red-500">*</span>
                      </label>
                      <div className="tw-relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="tw-mt-1 tw-px-2 tw-pr-10 input-outline tw-bg-white tw-border-[#CED4DA] tw-focus:ring-ln-blue tw-focus:border-ln-blue tw-block tw-w-full tw-shadow-sm tw-sm:text-sm tw-h-11 tw-rounded-md"
                          placeholder="Re-enter your new password"
                        />
                        <button
                          type="button"
                          className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-px-2 tw-text-gray-500 hover:tw-text-gray-700 tw-mt-1"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                        >
                          {showConfirmPassword ? (
                            <MdVisibilityOff className="tw-h-5 tw-w-5" />
                          ) : (
                            <MdVisibility className="tw-h-5 tw-w-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="tw-w-1/2">
                    <div>
                      <label className="tw-block tw-text-lg tw-font-medium tw-text-black">
                        New Password <span className="tw-text-red-500">*</span>
                      </label>
                      <div className="tw-relative">
                        <input
                          type={showNewPassword ? "text" : "password"}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="tw-mt-1 tw-px-2 tw-pr-10 input-outline tw-bg-white tw-border-[#CED4DA] tw-focus:ring-ln-blue tw-focus:border-ln-blue tw-block tw-w-full tw-shadow-sm tw-sm:text-sm tw-h-11 tw-rounded-md"
                          placeholder="Enter your new password"
                        />
                        <button
                          type="button"
                          className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-px-2 tw-text-gray-500 hover:tw-text-gray-700 tw-mt-1"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? (
                            <MdVisibilityOff className="tw-h-5 tw-w-5" />
                          ) : (
                            <MdVisibility className="tw-h-5 tw-w-5" />
                          )}
                        </button>
                      </div>
                      {newPassword && (
                        <div className="tw-mt-3 tw-p-3 tw-bg-gray-50 tw-rounded-md tw-border">
                          <p className="tw-font-medium tw-text-gray-700 tw-mb-2">
                            Password Requirements:
                          </p>
                          <div className="tw-grid tw-grid-cols-1 tw-gap-1 tw-text-sm">
                            <div
                              className={`tw-flex tw-items-center tw-space-x-2 ${
                                newPassword.length >= 8
                                  ? "tw-text-green-600"
                                  : "tw-text-red-500"
                              }`}
                            >
                              <span className="tw-font-bold">
                                {newPassword.length >= 8 ? "✓" : "✗"}
                              </span>
                              <span>At least 8 characters</span>
                            </div>
                            <div
                              className={`tw-flex tw-items-center tw-space-x-2 ${
                                /[A-Z]/.test(newPassword)
                                  ? "tw-text-green-600"
                                  : "tw-text-red-500"
                              }`}
                            >
                              <span className="tw-font-bold">
                                {/[A-Z]/.test(newPassword) ? "✓" : "✗"}
                              </span>
                              <span>At least one uppercase letter (A-Z)</span>
                            </div>
                            <div
                              className={`tw-flex tw-items-center tw-space-x-2 ${
                                /[a-z]/.test(newPassword)
                                  ? "tw-text-green-600"
                                  : "tw-text-red-500"
                              }`}
                            >
                              <span className="tw-font-bold">
                                {/[a-z]/.test(newPassword) ? "✓" : "✗"}
                              </span>
                              <span>At least one lowercase letter (a-z)</span>
                            </div>
                            <div
                              className={`tw-flex tw-items-center tw-space-x-2 ${
                                /\d/.test(newPassword)
                                  ? "tw-text-green-600"
                                  : "tw-text-red-500"
                              }`}
                            >
                              <span className="tw-font-bold">
                                {/\d/.test(newPassword) ? "✓" : "✗"}
                              </span>
                              <span>At least one number (0-9)</span>
                            </div>
                            <div
                              className={`tw-flex tw-items-center tw-space-x-2 ${
                                /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(
                                  newPassword
                                )
                                  ? "tw-text-green-600"
                                  : "tw-text-red-500"
                              }`}
                            >
                              <span className="tw-font-bold">
                                {/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(
                                  newPassword
                                )
                                  ? "✓"
                                  : "✗"}
                              </span>
                              <span>
                                At least one special character (!@#$%^&*)
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="tw-px-8 tw-py-8 tw-flex tw-justify-end tw-border-t tw-border-gray-200">
                <button
                  type="button"
                  className="tw-bg-white tw-text-black tw-px-4 tw-py-2 tw-rounded tw-border tw-border-[#CED4DA] tw-mr-2 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="tw-bg-[#0d6efd] tw-text-white tw-px-4 tw-py-2 tw-rounded tw-border tw-border-[#0d6efd] disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                  onClick={handleChangePassword}
                  disabled={isLoading}
                >
                  {isLoading ? "Updating..." : "Update Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <Toaster position="bottom-right" className="tw-mr-4" />
    </>
  );
};

export default ChangePassword;
